import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Paper,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { calculateWebSearchCost, normalizeSearchCallCount } from "../utils/llmCost";

function getTracePromptValue(promptContent, camelCaseName, pascalCaseName) {
  return promptContent?.[camelCaseName] ?? promptContent?.[pascalCaseName];
}

function normalizeTraceStringList(value) {
  if (Array.isArray(value)) {
    return value.filter((item) => typeof item === "string" && item.trim());
  }

  return typeof value === "string" && value.trim() ? [value] : [];
}

function normalizeTraceBoolean(value) {
  if (typeof value === "string") {
    const normalizedValue = value.trim().toLowerCase();
    if (normalizedValue === "true") return true;
    if (normalizedValue === "false") return false;
  }

  return Boolean(value);
}

function CollapsibleTraceField({ label, preview, children }) {
  const [expanded, setExpanded] = React.useState(false);

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      sx={{
        border: "1px solid rgba(15, 23, 42, 0.08)",
        borderRadius: "12px !important",
        backgroundColor: "#fff",
        "&:before": { display: "none" },
        "& + &": { mt: 0.75 },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon fontSize="small" />}
        sx={{
          minHeight: 40,
          px: 1.25,
          "&.Mui-expanded": { minHeight: 40 },
          "& .MuiAccordionSummary-content": {
            minWidth: 0,
            my: 0.75,
            "&.Mui-expanded": { my: 0.75 },
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, width: "100%" }}>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, fontWeight: 700 }}>
            {label}
          </Typography>
          {!expanded && (
            <Typography variant="body2" noWrap sx={{ minWidth: 0, color: "#374151" }}>
              {preview}
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.25, pt: 0.25, pb: 1.25 }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

export default function LlmTraceDetails({ promptContent }) {
  const reasoningSummary = getTracePromptValue(
    promptContent,
    "reasoningSummary",
    "ReasoningSummary"
  );
  const webSearchValue = getTracePromptValue(promptContent, "webSearch", "WebSearch");
  const webSearchDetails = getTracePromptValue(
    promptContent,
    "webSearchDetails",
    "WebSearchDetails"
  );
  const queries = normalizeTraceStringList(
    getTracePromptValue(webSearchDetails, "queries", "Queries")
  );
  const sources = normalizeTraceStringList(
    getTracePromptValue(webSearchDetails, "sources", "Sources")
  );
  const searchStatus = getTracePromptValue(webSearchDetails, "status", "Status");
  const searchCallCountValue = getTracePromptValue(
    webSearchDetails,
    "searchCallCount",
    "SearchCallCount"
  );
  const searchCallCount = normalizeSearchCallCount(searchCallCountValue);
  const searchCallCost = calculateWebSearchCost(searchCallCount);
  const hasSearchCallCount = searchCallCountValue !== undefined && searchCallCountValue !== null;
  const hasMeaningfulWebSearchDetails = Boolean(
    searchStatus || searchCallCount > 0 || queries.length > 0 || sources.length > 0
  );
  const webSearchWasUsed =
    normalizeTraceBoolean(webSearchValue) || hasMeaningfulWebSearchDetails;

  if (!reasoningSummary && !webSearchWasUsed) {
    return null;
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.75,
        borderRadius: "18px",
        backgroundColor: "rgba(255,255,255,0.84)",
        borderColor: "rgba(255, 132, 76, 0.10)",
        boxShadow: "0 14px 30px rgba(236, 123, 55, 0.05)",
      }}
    >
      <Typography variant="subtitle2" fontWeight={800} sx={{ color: "#202531", mb: 1 }}>
        Reasoning &amp; Web Search
      </Typography>

      {reasoningSummary && (
        <Box sx={{ mb: 1.25 }}>
          <CollapsibleTraceField
            label="Reasoning Summary"
            preview={String(reasoningSummary).replace(/\s+/g, " ").trim()}
          >
            <Typography
              variant="body2"
              sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
            >
              {String(reasoningSummary)}
            </Typography>
          </CollapsibleTraceField>
        </Box>
      )}

      {webSearchWasUsed && (
        <Box>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1,
              mb: queries.length || sources.length ? 1 : 0,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Web Search
            </Typography>
            <Chip
              size="small"
              label={webSearchWasUsed ? "Used" : "Not used"}
              color={webSearchWasUsed ? "success" : "default"}
              variant={webSearchWasUsed ? "filled" : "outlined"}
            />
            {searchStatus && (
              <Chip size="small" label={`Status: ${searchStatus}`} variant="outlined" />
            )}
            {hasSearchCallCount && (
              <>
                <Chip size="small" label={`Search calls: ${searchCallCount}`} variant="outlined" />
                <Chip
                  size="small"
                  label={`Search cost: $${searchCallCost.toFixed(6)}`}
                  variant="outlined"
                />
              </>
            )}
          </Box>

          {queries.length > 0 && (
            <Box sx={{ mb: sources.length ? 1.25 : 0 }}>
              <CollapsibleTraceField
                label={`Queries (${queries.length})`}
                preview={queries[0]}
              >
                <Box component="ul" sx={{ my: 0, pl: 2.5 }}>
                  {queries.map((query, index) => (
                    <Typography
                      component="li"
                      variant="body2"
                      key={`${query}-${index}`}
                      sx={{ overflowWrap: "anywhere" }}
                    >
                      {query}
                    </Typography>
                  ))}
                </Box>
              </CollapsibleTraceField>
            </Box>
          )}

          {sources.length > 0 && (
            <CollapsibleTraceField
              label={`Sources (${sources.length})`}
              preview={sources[0]}
            >
              <Box component="ul" sx={{ my: 0, pl: 2.5 }}>
                {sources.map((source, index) => (
                  <Typography
                    component="li"
                    variant="body2"
                    key={`${source}-${index}`}
                    sx={{ overflowWrap: "anywhere" }}
                  >
                    <Box
                      component="a"
                      href={source}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: "#ea580c", overflowWrap: "anywhere" }}
                    >
                      {source}
                    </Box>
                  </Typography>
                ))}
              </Box>
            </CollapsibleTraceField>
          )}
        </Box>
      )}
    </Paper>
  );
}
